import { Layout } from "@/components/layout/Layout";
import { getStrapiCollection, getStrapiEntity } from "@/utils/strapi";
import {
  LoadI18nMessagesProps,
  loadIntlMessages,
} from "@/utils/loadIntlMessages";
import { createQueryCityPage } from "@/strapiQuery/cityPage";
import { page404 } from "@/const/page404";
import { formatFirstCityPage, StrapiCityPage } from "@/utils/formatCityPage";
import { NetworkError } from "@/interfaces/errors/networkError";
import { sendFetchRequest } from "@/utils/sendFetchRequest";
import { CityDetails } from "@/interfaces/cityDetails";
import { queryDefaultPresentation } from "@/strapiQuery/default-presentation";
import { DefaultPresentationProps } from "@/components/common/defaultPresentation/DefaultPresentation";
import { routes } from "@/routes/routes";
import { ReviewsConst } from "@/components/common/reviewsBlock/ReviewsBlock";
import { queryReviewsConst } from "@/strapiQuery/queryReviewsConst";

type GetStaticPropsParams = LoadI18nMessagesProps & {
  params: { slug: string };
};

const SLUG_NOT_FOUND_ERROR_CODE = 422;

export const getStaticProps = async (ctx: GetStaticPropsParams) => {
  const layout = await getStrapiEntity<Layout>("layout", "");
  const reviewsConst = await getStrapiEntity<ReviewsConst>(
    "reviews-const",
    queryReviewsConst
  );

  const intlMessages = loadIntlMessages(ctx);

  let city: CityDetails;
  try {
    city = await sendFetchRequest(`/cities/v1/${ctx.params.slug}`).then(
      (json: CityDetails) => json
    );
  } catch (error) {
    if (
      error instanceof NetworkError &&
      error.statusCode === SLUG_NOT_FOUND_ERROR_CODE
    ) {
      return page404;
    }
    throw error;
  }

  const strapiDefaultPresentation =
    await getStrapiEntity<DefaultPresentationProps>(
      "default-city-presentation",
      queryDefaultPresentation
    );

  const strapiCityPages = await getStrapiCollection<StrapiCityPage>(
    "city-pages",
    createQueryCityPage(ctx.params.slug)
  );

  if (strapiCityPages.length === 0) {
    return page404;
  }
  const agency = city.stateAgencies.find((obj) => obj.name === city.name);
  const CanonicalUrl =
    agency !== undefined
      ? routes.agencyPage(agency.slug)
      : routes.cityPage(ctx.params.slug);
  const cityPageContent = formatFirstCityPage(strapiCityPages);

  return {
    props: {
      layout,
      reviewsConst,
      intlMessages,
      city,
      heroBlock: cityPageContent.heroBlock,
      defaultPresentation: strapiDefaultPresentation,
      slug: ctx.params.slug,
      pointOfInterestList: cityPageContent.pointOfInterestList,
      cityDescription: cityPageContent.cityDescription,
      faq: cityPageContent.faq,
      seo: cityPageContent.seo,
      seoWithTemplate: cityPageContent.seoWithTemplate,
      defaultCanonicalRoute: CanonicalUrl,
    },
    revalidate: 10,
  };
};

export const getStaticPaths = () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export { CityPage as default } from "@/components/pages/agencies/cityPage/CityPage";
